import type { Company } from '../interfaces';
import type { Invoicing, TaxData } from '@/types/parcel/userData/profile.interface';

/**
 * Transforma datos de company a formato invoicing para México
 * @param company - Datos de la empresa del formulario
 * @returns Objeto con formato invoicing para el API
 */

export const formatCompanyToInvoicingMEX = (company: Company): Invoicing => {
  const needsInvoice = company.requireInvoice ?? false;

  // Si no necesita factura, enviar solo los campos básicos sin type ni regimeType
  if (!needsInvoice) {
    const taxData: TaxData = {
      legalName: company.legalName || company.name,
      email: company.email,
      phone: company.phone,
    };

    return {
      needsInvoice: false,
      companyName: company.name,
      taxData,
    };
  }

  // Si necesita factura, enviar todos los campos
  const taxData: TaxData = {
    type: 'RFC',
    legalName: company.legalName || company.name,
    number: company.rfc,
    email: company.email,
    address: company.address,
    phone: company.phone,
    zipcode: company.zipCode,
    regimeCode: company.taxRegime,
    regimeType: company.companyType as 'naturalPerson' | 'corporateEntity',
    invoiceUsageCode: company.cfdiUse,
  };

  // Agregar descripciones si existen (mismo patrón que client-console)
  if (company.taxRegime && company.taxRegimeDescription) {
    taxData.regimeDescription = company.taxRegimeDescription;
  }
  if (company.cfdiUse && company.cfdiDescription) {
    taxData.invoiceUsageDescription = company.cfdiDescription;
  }

  // Agregar documento CSF como base64 si existe
  if (company.proofTaxSituationFile) {
    taxData.document = company.proofTaxSituationFile; // Base64 string
  }

  return {
    needsInvoice: true,
    companyName: company.name,
    taxData,
  };
};

/**
 * Transforma datos de company a formato invoicing para otros países (Colombia, Chile, Perú)
 * @param company - Datos de la empresa del formulario
 * @param country - Código del país (COL, CHL, PER)
 * @returns Objeto con formato invoicing para el API
 */

export const formatCompanyToInvoicingOtherCountries = (
  company: Company,
  country: string,
): Invoicing => {
  const taxTypeMap: Record<string, 'NIT' | 'RUT' | 'RUC'> = {
    COL: 'NIT',
    CHL: 'RUT',
    PER: 'RUC',
  };

  const taxData: TaxData = {
    type: taxTypeMap[country] || 'NIT',
    legalName: company.name,
    number: company.rfc || company.identification,
    email: company.email,
    address: company.address,
    phone: company.phone,
    regimeType: company.companyType || '',
  };

  // Perú no incluye zipcode
  if (country !== 'PER') {
    taxData.zipcode = company.zipCode;
  }

  // Para Colombia, agregar regimeDescription si existe
  if (country === 'COL' && company.regimeDescription) {
    taxData.regimeDescription = company.regimeDescription;
  }

  // Para Chile, agregar invoiceUsageDescription si existe
  if (country === 'CHL' && company.invoiceUsageDescription) {
    taxData.invoiceUsageDescription = company.invoiceUsageDescription;
  }

  return {
    needsInvoice: true,
    taxData,
  };
};

/**
 * Transforma company a invoicing según el país
 * @param company - Datos de la empresa del formulario
 * @param country - Código del país (MEX, COL, CHL, PER)
 * @returns Objeto con formato invoicing para el API
 */

export const formatCompanyToInvoicing = (company: Company, country: string): Invoicing => {
  return country === 'MEX'
    ? formatCompanyToInvoicingMEX(company)
    : formatCompanyToInvoicingOtherCountries(company, country);
};
